// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRippleDelegated } from "@/hooks/use-ripple";
import { cn } from "@/lib/utils";

const ITEM_HEIGHT = 36;

interface VirtualMultiSelectOption {
  label: string;
  value: string;
}

interface VirtualMultiSelectPopoverProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: readonly VirtualMultiSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  className?: string;
  dirty?: boolean;
}

export function VirtualMultiSelectPopover({
  values,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  className,
  dirty,
}: VirtualMultiSelectPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  // Snapshot of the selected values taken when the popover opens — used to pin selected items to
  // the top without reordering as the user toggles them during the same session.
  const [pinnedSelected, setPinnedSelected] = useState<Set<string>>(new Set());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerHeight, setTriggerHeight] = useState(36);

  useRippleDelegated(scrollEl);

  const selectedSet = useMemo(() => new Set(values), [values]);

  useEffect(
    function scrollToTopOnOpen() {
      if (open && scrollEl) scrollEl.scrollTop = 0;
    },
    [open, scrollEl],
  );

  useEffect(
    function observeTriggerHeight() {
      const el = triggerRef.current;
      if (!open || !el) return;
      setTriggerHeight(el.offsetHeight);
      const observer = new ResizeObserver(() => {
        setTriggerHeight(el.offsetHeight);
      });
      observer.observe(el);
      return () => observer.disconnect();
    },
    [open],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q === "" ? options : options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  // While not searching, pin the items that were selected when the popover opened to the top
  const ordered = useMemo(() => {
    if (search !== "" || pinnedSelected.size === 0) return filtered;
    const selected = filtered.filter((o) => pinnedSelected.has(o.value));
    const rest = filtered.filter((o) => !pinnedSelected.has(o.value));
    return selected.length > 0 ? [...selected, ...rest] : filtered;
  }, [filtered, search, pinnedSelected]);

  useEffect(function resetFocusIndexOnMount() {
    setFocusedIndex(-1);
  }, []);

  const virtualizer = useVirtualizer({
    count: ordered.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 8,
  });

  function toggle(option: VirtualMultiSelectOption) {
    if (selectedSet.has(option.value)) {
      onChange(values.filter((v) => v !== option.value));
    } else {
      onChange([...values, option.value]);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(focusedIndex + 1, ordered.length - 1);
      setFocusedIndex(next);
      virtualizer.scrollToIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(focusedIndex - 1, 0);
      setFocusedIndex(prev);
      virtualizer.scrollToIndex(prev);
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      e.preventDefault();
      const item = ordered[focusedIndex];
      if (item) toggle(item);
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(() => setSearch(""), 150);
        if (o) {
          setFocusedIndex(-1);
          setPinnedSelected(new Set(values));
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          className={cn("w-full justify-between font-normal h-auto min-h-9 py-1", className)}
        >
          {values.length === 0 ? (
            <span className="text-muted-foreground truncate py-px">{placeholder}</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {values.map((v) => {
                const label = options.find((o) => o.value === v)?.label ?? v;
                return (
                  <Badge
                    key={v}
                    variant="secondary"
                    className={cn("gap-1 pr-1 text-sm font-normal", dirty && "bg-foreground/10")}
                  >
                    {label}
                    <button
                      type="button"
                      data-no-ripple
                      className="rounded-sm hover:bg-foreground/20 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(values.filter((val) => val !== v));
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      tabIndex={-1}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        side="bottom"
        sideOffset={-triggerHeight}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div
          ref={setScrollEl}
          className="overflow-y-auto rounded-b-md"
          style={{ height: Math.min(ordered.length * ITEM_HEIGHT, 220) }}
          onWheel={(e) => e.stopPropagation()}
        >
          {ordered.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((vItem) => {
                const option = ordered[vItem.index];
                if (!option) return null;
                const isSelected = selectedSet.has(option.value);
                const isFocused = vItem.index === focusedIndex;
                return (
                  <div
                    key={option.value}
                    data-ripple
                    style={{ position: "absolute", top: vItem.start, left: 0, right: 0, height: ITEM_HEIGHT }}
                    className={cn(
                      "relative overflow-hidden flex items-center gap-2 px-2 cursor-pointer text-sm hover:bg-accent",
                      isFocused ? "bg-accent" : isSelected ? "bg-accent/60" : "",
                    )}
                    onClick={() => toggle(option)}
                  >
                    <span className="flex-1 truncate">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
